import { CSSProperties, MouseEvent, useState } from 'react';
import { Col, Divider, Modal, Row, Space, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface ScrapTier {
	index: number;
	lower: number;
	upper: number | null;
}

export interface ScrapBreakdown {
	quantity: number;
	cost_per_piece: number | null;
	estimated_scrap_quantity: number;
	used_fallback: boolean;
	fallback_reason: string | null;
	fallback_percent?: number;
	quantity_tier?: ScrapTier | null;
	cost_tier?: ScrapTier | null;
	quantity_tiers?: number[];
	cost_tiers?: number[];
	scrap_grid?: number[][];
	manual?: boolean;
	// PTB-5965 order-level scrap allocation. `order_scrap_allowance` is the full
	// scrap the table permits for the order quantity; `already_allocated_scrap`
	// is how much of it earlier reservations for the same line item already took.
	// `estimated_scrap_quantity` on this breakdown is the REMAINING allowance to
	// use for the current reservation (allowance minus already allocated).
	order_scrap_allowance?: number;
	already_allocated_scrap?: number;
}

function formatQuantityTier(tier?: ScrapTier | null): string {
	if (!tier) return '';
	if (tier.upper == null) return `≥ ${tier.lower}`;
	const high = tier.upper - 1;
	return high === tier.lower ? `${tier.lower}` : `${tier.lower} – ${high}`;
}

function formatCostTier(tier?: ScrapTier | null): string {
	if (!tier) return '';
	const money = (value: number) => `$${Number(value).toFixed(2)}`;
	if (tier.index === 0) {
		return tier.upper != null ? `< ${money(tier.upper)}` : `≥ ${money(tier.lower)}`;
	}
	if (tier.upper == null) return `≥ ${money(tier.lower)}`;
	return `${money(tier.lower)} – < ${money(tier.upper)}`;
}

function ScrapTable({ b }: { b: ScrapBreakdown }) {
	const grid = b.scrap_grid;
	const qTiers = b.quantity_tiers;
	const cTiers = b.cost_tiers;
	if (!grid || !qTiers || !cTiers) return null;

	const activeRow = b.quantity_tier?.index ?? -1;
	const activeCol = b.cost_tier?.index ?? -1;

	const cell: CSSProperties = {
		border: '1px solid #f0f0f0',
		padding: '6px 12px',
		textAlign: 'center',
		fontSize: 14,
		whiteSpace: 'nowrap',
	};
	const header: CSSProperties = {
		...cell,
		background: '#fafafa',
		fontWeight: 600,
	};

	return (
		<div style={{ overflowX: 'auto', marginTop: 10 }}>
			<table
				style={{
					borderCollapse: 'collapse',
					margin: '0 auto',
					width: '100%',
				}}
			>
				<thead>
					<tr>
						<th style={header}>Qty / $pc</th>
						{cTiers.map((c, ci) => (
							<th
								key={ci}
								style={{
									...header,
									background: ci === activeCol ? '#e6f7ff' : '#fafafa',
								}}
							>
								${c}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{grid.map((row, ri) => (
						<tr key={ri}>
							<th
								style={{
									...header,
									background: ri === activeRow ? '#e6f7ff' : '#fafafa',
								}}
							>
								{qTiers[ri]}
							</th>
							{row.map((val, ci) => {
								const isActive = ri === activeRow && ci === activeCol;
								const inCross = ri === activeRow || ci === activeCol;
								return (
									<td
										key={ci}
										style={{
											...cell,
											background: isActive
												? '#1890ff'
												: inCross
												? '#e6f7ff'
												: '#fff',
											color: isActive ? '#fff' : undefined,
											fontWeight: isActive ? 700 : undefined,
										}}
									>
										{val}
									</td>
								);
							})}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function ScrapExplanation({ b }: { b: ScrapBreakdown | null }) {
	const pct = Math.round((b?.fallback_percent ?? 0.15) * 100);

	return (
		<div style={{ width: '100%' }}>
			<Text type="secondary" style={{ fontSize: 14 }}>
				For standard production the scrap is determined from a lookup table
				indexed by the order quantity and the material cost per piece.
			</Text>
			<Divider style={{ margin: '8px 0' }} />

			{!b ? (
				<Text style={{ fontSize: 14 }}>
					Not calculated yet. Enter the cut length and quantity to compute
					the scrap from the table.
				</Text>
			) : b.manual ? (
				<Space direction="vertical" size={2}>
					<Text strong style={{ fontSize: 14 }}>
						Manually edited value
					</Text>
					<Text style={{ fontSize: 14 }}>
						Current scrap: <Text strong>{b.estimated_scrap_quantity}</Text> pcs
					</Text>
					<Text type="secondary" style={{ fontSize: 14 }}>
						Change the quantity or cut length to recompute it from the
						table.
					</Text>
				</Space>
			) : b.used_fallback ? (
				<Space direction="vertical" size={2}>
					<Text strong style={{ fontSize: 14 }}>
						Source: smart fallback (no cost per piece)
					</Text>
					<Text type="secondary" style={{ fontSize: 14 }}>
						The cost per piece could not be estimated (missing dimensions,
						cut length, or material cost). The fallback is the lower of{' '}
						{pct}% of the quantity (rounded up) and the table's maximum
						scrap for this quantity, so we never cut more extras than the
						table would.
					</Text>
					<Text style={{ fontSize: 14 }}>
						Order quantity: <Text strong>{b.quantity}</Text> · Result:{' '}
						<Text strong>{b.estimated_scrap_quantity}</Text> pcs
					</Text>
				</Space>
			) : (
				<Space direction="vertical" size={4}>
					<Text strong style={{ fontSize: 14 }}>
						Source: lookup table
					</Text>
					<Row gutter={8} style={{ fontSize: 14 }}>
						<Col span={14}>Order quantity</Col>
						<Col span={10} style={{ textAlign: 'right' }}>
							<Text strong>{b.quantity}</Text>
						</Col>
						<Col span={14}>
							<Text type="secondary">Row range</Text>
						</Col>
						<Col span={10} style={{ textAlign: 'right' }}>
							<Text type="secondary">
								{formatQuantityTier(b.quantity_tier)}
							</Text>
						</Col>
						<Col span={14}>Estimated cost per piece</Col>
						<Col span={10} style={{ textAlign: 'right' }}>
							<Text strong>
								{b.cost_per_piece != null
									? `$${Number(b.cost_per_piece).toFixed(2)}`
									: '—'}
							</Text>
						</Col>
						<Col span={14}>
							<Text type="secondary">Column range</Text>
						</Col>
						<Col span={10} style={{ textAlign: 'right' }}>
							<Text type="secondary">{formatCostTier(b.cost_tier)}</Text>
						</Col>
					</Row>
					<Divider style={{ margin: '4px 0' }} />
					<Row style={{ fontSize: 14 }}>
						<Col span={14}>Suggested scrap (table)</Col>
						<Col span={10} style={{ textAlign: 'right' }}>
							<Text strong>{b.estimated_scrap_quantity}</Text> pcs
						</Col>
					</Row>
				</Space>
			)}

			{b &&
				b.order_scrap_allowance != null &&
				(b.already_allocated_scrap ?? 0) > 0 && (
					<>
						<Divider style={{ margin: '8px 0' }} />
						<Space direction="vertical" size={2}>
							<Text strong style={{ fontSize: 14 }}>
								Allocation across reservations
							</Text>
							<Text type="secondary" style={{ fontSize: 14 }}>
								Scrap is an order-level allowance: the whole order shares
								the table value, so it is only reserved once. Later
								reservations for this line item get just what is left.
							</Text>
							<Row gutter={8} style={{ fontSize: 14 }}>
								<Col span={16}>Order scrap allowance (table)</Col>
								<Col span={8} style={{ textAlign: 'right' }}>
									<Text strong>{b.order_scrap_allowance}</Text> pcs
								</Col>
								<Col span={16}>Already reserved on other reservations</Col>
								<Col span={8} style={{ textAlign: 'right' }}>
									<Text strong>{b.already_allocated_scrap}</Text> pcs
								</Col>
								<Col span={16}>Remaining for this reservation</Col>
								<Col span={8} style={{ textAlign: 'right' }}>
									<Text strong>{b.estimated_scrap_quantity}</Text> pcs
								</Col>
							</Row>
						</Space>
					</>
				)}

			{b && b.scrap_grid && (
				<>
					<Divider style={{ margin: '8px 0' }} />
					<Text type="secondary" style={{ fontSize: 13 }}>
						Full table (scrap pieces). The highlighted cell is your result;
						rows = quantity, columns = cost per piece.
					</Text>
					<ScrapTable b={b} />
				</>
			)}
		</div>
	);
}

interface ScrapInfoButtonProps {
	breakdown: ScrapBreakdown | null;
	iconStyle?: CSSProperties;
	/**
	 * When provided the trigger is rendered as a plain text link instead of an
	 * info icon (e.g. "View calculation").
	 */
	triggerText?: string;
	linkStyle?: CSSProperties;
}

/**
 * Trigger that opens a modal explaining how the scrap value was derived (lookup
 * table vs fallback), including the full table with the resolved cell
 * highlighted. Rendered as a text link when `triggerText` is set, otherwise as
 * an info icon.
 */
const ScrapInfoButton = ({
	breakdown,
	iconStyle,
	triggerText,
	linkStyle,
}: ScrapInfoButtonProps) => {
	const [open, setOpen] = useState(false);

	const openModal = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setOpen(true);
	};

	return (
		<>
			{triggerText ? (
				<a onClick={openModal} style={linkStyle}>
					{triggerText}
				</a>
			) : (
				<InfoCircleOutlined
					style={{
						color: '#1890ff',
						cursor: 'pointer',
						fontSize: 16,
						...iconStyle,
					}}
					onClick={openModal}
				/>
			)}
			<Modal
				title="How is the scrap calculated?"
				open={open}
				width={920}
				centered
				onOk={() => setOpen(false)}
				onCancel={() => setOpen(false)}
				okText="Ok"
				cancelButtonProps={{ style: { display: 'none' } }}
			>
				<ScrapExplanation b={breakdown} />
			</Modal>
		</>
	);
};

export default ScrapInfoButton;
